import { useRef, useState } from 'react'
import { Box, Flex, Text, VStack } from '@chakra-ui/react'
import { LuStore, LuCheck, LuTriangleAlert } from 'react-icons/lu'
import { useNavigate } from 'react-router-dom'
import { DataTable, DataList, PageHeader, toaster, ConfirmDialog } from '@minetrace/ui'
import { brand, themeColors } from '../../theme'
import { proveedoresService, companiesService, modificationRequestService } from '../../api/services'
import { usePagedRequest } from '../../hooks'
import type { ProveedorListItemResponse } from '../../api/models/response/proveedor'
import { exportToExcel, type ExcelColumn } from '../../utils/exportExcel'
import { useT } from '../../i18n'
import { useHasPermission } from '../../auth/PermissionContext'

const REG_STATUS_CONFIG: Record<number, { label: string; bg: string; color: string }> = {
  0: { label: 'Borrador', bg: 'rgba(181, 170, 157, 0.2)', color: '#857b71' },
  1: { label: 'En Evaluación', bg: 'rgba(61, 111, 168, 0.2)', color: '#3d6fa8' },
  2: { label: 'Observado', bg: 'rgba(212, 154, 42, 0.2)', color: '#d49a2a' },
  3: { label: 'Aprobado', bg: 'rgba(42, 138, 135, 0.2)', color: '#2a8a87' },
}

const REGISTRADO_BADGE = { label: 'Registrado', bg: 'rgba(42, 138, 135, 0.18)', color: '#1d6664' }

function resolveStatusBadge(e: ProveedorListItemResponse) {
  if (e.registrationStatus === 0 && e.wizardCompletedAt) return REGISTRADO_BADGE
  return REG_STATUS_CONFIG[e.registrationStatus] ?? REG_STATUS_CONFIG[0]!
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('es-PE')
}

const excelColumns: ExcelColumn<ProveedorListItemResponse>[] = [
  { header: 'Fecha Registro', accessor: (e) => formatDate(e.createdAt) },
  { header: 'RUC', accessor: (e) => e.ruc },
  { header: 'Razon Social', accessor: (e) => e.legalName },
  { header: 'Estado REINFO', accessor: (e) => e.reinfoStatus ?? '' },
  { header: 'Derechos Mineros', accessor: (e) => String(e.miningRightCount) },
  { header: 'IGAFOM', accessor: (e) => e.hasIgafomDocument ? 'Si' : 'No' },
  { header: 'Ult. DPS', accessor: (e) => e.hasDpsDocument ? 'Si' : 'No' },
  { header: 'Tiene RECPO', accessor: (e) => e.hasRecpo ? 'Si' : 'No' },
  { header: 'Estado', accessor: (e) => resolveStatusBadge(e).label },
  { header: 'Fecha Aprobacion', accessor: (e) => formatDate(e.approvedAt) },
]

export default function ProveedoresPage() {
  const canCreateMiner = useHasPermission('miners.create')
  const colors = themeColors
  const navigate = useNavigate()
  const { t } = useT()

  const [search, setSearch] = useState('')
  const [creatingMROn, setCreatingMROn] = useState<ProveedorListItemResponse | null>(null)
  const [createMRReason, setCreateMRReason] = useState('')
  const [creatingMR, setCreatingMR] = useState(false)
  const [deletingOn, setDeletingOn] = useState<ProveedorListItemResponse | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [reacceptOn, setReacceptOn] = useState<ProveedorListItemResponse | null>(null)
  const [reaccepting, setReaccepting] = useState(false)
  // Guard síncrono contra doble envío: el menú de acciones no se cierra al hacer
  // clic, así que un doble clic disparaba 2 peticiones (la 2ª fallaba porque la
  // 1ª ya movió el minero de Draft a PendingReview).
  const submittingIds = useRef<Set<string>>(new Set())

  const { items: empresas, totalCount, isFetching: loading, error, refresh, page, setPage, totalPages, pageSize, setPageSize } = usePagedRequest(
    proveedoresService.getAll,
    { params: { search: search.trim() || undefined } },
  )

  // Exporta el directorio completo, no la página visible. El filtro de búsqueda viaja al
  // servidor: exportar "todo" tiene que respetar lo que el usuario está filtrando.
  const handleExportAll = async () => {
    try {
      const todos = await proveedoresService.exportAll(search.trim() || undefined)
      if (todos.length === 0) {
        toaster.error({ description: 'No hay mineros que coincidan con el filtro' })
        return
      }
      exportToExcel(todos, excelColumns, 'proveedores')
    } catch (e) {
      toaster.error({ description: e instanceof Error ? e.message : 'No se pudo exportar el directorio' })
    }
  }

  const handleCreateMR = async () => {
    if (!creatingMROn) return
    if (!createMRReason.trim()) {
      toaster.error({ description: 'Debe ingresar un motivo' })
      return
    }
    setCreatingMR(true)
    try {
      // Fetch proveedor to resolve its expedienteCode (not in list DTO)
      const p = await proveedoresService.getById(creatingMROn.id)
      if (!p.cuid?.expedienteCode) {
        throw new Error('Este minero aún no tiene expediente')
      }
      const mrId = await modificationRequestService.create(p.cuid.expedienteCode, createMRReason.trim())
      toaster.success({ description: 'Solicitud creada. Abriendo editor...' })
      navigate(`/app/proveedores/${creatingMROn.id}/modificar/${mrId}`)
    } catch (e) {
      toaster.error({ description: e instanceof Error ? e.message : 'Error al crear la solicitud' })
    } finally {
      setCreatingMR(false)
    }
  }

  const closeCreateMR = () => {
    if (creatingMR) return
    setCreatingMROn(null)
    setCreateMRReason('')
  }

  const handleConfirmDelete = async () => {
    if (!deletingOn) return
    setDeleting(true)
    try {
      await companiesService.delete(deletingOn.id)
      toaster.success({ description: `${deletingOn.legalName} eliminado` })
      setDeletingOn(null)
      refresh()
    } catch (e) {
      toaster.error({ description: e instanceof Error ? e.message : 'Error al eliminar el proveedor' })
    } finally {
      setDeleting(false)
    }
  }

  const handleConfirmReacceptance = async () => {
    if (!reacceptOn) return
    setReaccepting(true)
    try {
      await proveedoresService.requestConsentReacceptance(reacceptOn.id)
      toaster.success({ description: `Se solicitó la re-aceptación del consentimiento a ${reacceptOn.legalName}` })
      setReacceptOn(null)
      refresh()
    } catch (e) {
      toaster.error({ description: e instanceof Error ? e.message : 'No se pudo solicitar la re-aceptación' })
    } finally {
      setReaccepting(false)
    }
  }

  const handleSubmitForEvaluation = async (empresa: ProveedorListItemResponse) => {
    if (submittingIds.current.has(empresa.id)) return
    submittingIds.current.add(empresa.id)
    try {
      await proveedoresService.submitForEvaluation(empresa.id)
      toaster.success({ description: `${empresa.legalName} enviado a evaluación` })
      refresh()
    } catch {
      // El cliente HTTP ya muestra el toaster de error; no lo duplicamos aquí.
    } finally {
      submittingIds.current.delete(empresa.id)
    }
  }

  const Badge = ({ label, bg, color }: { label: string; bg: string; color: string }) => (
    <Box display="inline-block" px={2} py={0.5} borderRadius="md" fontSize="10px" fontWeight="bold"
      textTransform="uppercase" letterSpacing="0.05em" bg={bg} color={color}>
      {label}
    </Box>
  )

  const Check = ({ value }: { value: boolean }) => (
    value
      ? <Box display="inline-flex" alignItems="center" justifyContent="center"><LuCheck size={18} color="#2a8a87" /></Box>
      : <Text fontSize="sm" color={colors.textMuted}>—</Text>
  )

  return (
    <VStack gap={6} alignItems="stretch">
      <PageHeader
        title={t('proveedores.title')}
        subtitle={t('proveedores.subtitle')}
        buttonLabel={canCreateMiner ? t('proveedores.newButton') : undefined}
        onButtonClick={canCreateMiner ? () => navigate('/app/proveedores/nuevo') : undefined}
        buttonIcon={canCreateMiner ? <LuStore size={18} /> : undefined}
        colors={colors}
      />

      <DataList<ProveedorListItemResponse>
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder={t('proveedores.searchPlaceholder')}
        onExport={() => exportToExcel(empresas, excelColumns, 'proveedores')}
        onExportAll={handleExportAll}
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
        totalCount={totalCount}
        pageSize={pageSize}
        onPageSizeChange={setPageSize}
        entityName="proveedores"
        brandColor={brand.green}
        // Las dos fechas van juntas para poder compararlas de un vistazo, y el RUC
        // abre la fila porque es el identificador con el que se busca al minero.
        headers={['RUC', 'Razón Social', 'REINFO', 'DM', 'IGAFOM', 'Ult. DPS', 'RECPO', 'Estado', 'Fecha Registro', 'Fecha Aprobación']}
        data={empresas}
        loading={loading}
        error={error}
        emptyMessage={t('proveedores.emptyMessage')}
        loadingMessage={t('proveedores.loadingMessage')}
        colors={colors}
        keyExtractor={(e) => e.id}
        renderRow={(e) => {
          const regStatus = resolveStatusBadge(e)

          return (
            <>
              <DataTable.Cell>
                <Text fontFamily="monospace" fontSize="sm" color={colors.text}>{e.ruc}</Text>
              </DataTable.Cell>
              <DataTable.Cell>
                <Text fontSize="sm" fontWeight="500" color={colors.text} wordBreak="break-word">{e.legalName}</Text>
              </DataTable.Cell>
              <DataTable.Cell>
                <Text fontSize="xs" color={colors.text}>{e.reinfoStatus ?? '—'}</Text>
              </DataTable.Cell>
              <DataTable.Cell>
                <Text fontSize="sm" fontWeight="600" color={colors.text} textAlign="center">{e.miningRightCount}</Text>
              </DataTable.Cell>
              <DataTable.Cell><Check value={e.hasIgafomDocument} /></DataTable.Cell>
              <DataTable.Cell><Check value={e.hasDpsDocument} /></DataTable.Cell>
              <DataTable.Cell><Check value={e.hasRecpo} /></DataTable.Cell>
              <DataTable.Cell>
                <Box display="flex" flexDirection="column" gap={1} alignItems="flex-start">
                  <Badge label={regStatus.label} bg={regStatus.bg} color={regStatus.color} />
                  {e.activeModificationRequest && (
                    <Box display="inline-block" px={2} py={0.5} borderRadius="md" fontSize="9px" fontWeight="bold"
                      textTransform="uppercase" letterSpacing="0.05em"
                      bg="rgba(212, 154, 42, 0.15)" color="#a8740e">
                      {e.activeModificationRequest.status === 0 ? 'MR BORRADOR' : 'MR PENDIENTE'} · {e.activeModificationRequest.code}
                    </Box>
                  )}
                  {e.consentOutdated && (
                    <Flex alignItems="center" gap={1} title="El consentimiento firmado corresponde a una versión anterior. Requiere re-aceptación.">
                      <LuTriangleAlert size={12} color="#a8740e" />
                      <Text fontSize="9px" fontWeight="bold" textTransform="uppercase" letterSpacing="0.05em" color="#a8740e">
                        Consent. desactualizado
                      </Text>
                    </Flex>
                  )}
                </Box>
              </DataTable.Cell>
              <DataTable.Cell>
                <Text fontSize="xs" color={colors.text}>{formatDate(e.createdAt)}</Text>
              </DataTable.Cell>
              <DataTable.Cell>
                <Text fontSize="xs" color={colors.text}>{formatDate(e.approvedAt)}</Text>
              </DataTable.Cell>
            </>
          )
        }}
        actions={(empresa) => {
          const rs = empresa.registrationStatus
          const reacceptAction = empresa.consentOutdated
            ? [{ label: 'Solicitar re-aceptación', onClick: () => setReacceptOn(empresa), color: '#a8740e' }]
            : []
          if (rs === 0) {
            const draftActions: Array<{ label: string; onClick: () => void; color?: string }> = [
              { label: 'Continuar registro', onClick: () => navigate(`/app/proveedores/${empresa.id}`) },
            ]
            if (empresa.wizardCompletedAt != null) {
              draftActions.push({
                label: 'Enviar a Evaluación',
                onClick: () => handleSubmitForEvaluation(empresa),
              })
            }
            draftActions.push(...reacceptAction)
            draftActions.push({ label: 'Eliminar', onClick: () => setDeletingOn(empresa), color: '#c8442f' })
            return draftActions
          }
          if (rs === 1 || rs === 2) return [
            { label: 'Evaluar', onClick: () => navigate(`/app/proveedores/${empresa.id}/evaluar`) },
            { label: 'Ver Detalle', onClick: () => navigate(`/app/proveedores/${empresa.id}/detalle`) },
            { label: 'Editar', onClick: () => navigate(`/app/proveedores/${empresa.id}`) },
            ...reacceptAction,
          ]
          return [
            { label: 'Ver Detalle', onClick: () => navigate(`/app/proveedores/${empresa.id}/detalle`) },
            ...(empresa.activeModificationRequest
              ? [{
                  label: `Continuar ${empresa.activeModificationRequest.code}`,
                  onClick: () => navigate(`/app/proveedores/${empresa.id}/modificar/${empresa.activeModificationRequest!.id}`),
                }]
              : [{
                  label: 'Solicitar modificación',
                  onClick: () => setCreatingMROn(empresa),
                }]),
            ...reacceptAction,
            { label: 'Modificaciones', onClick: () => navigate(`/app/proveedores/${empresa.id}/modificaciones`) },
          ]
        }}
      />

      <ConfirmDialog
        open={!!reacceptOn}
        onClose={() => { if (!reaccepting) setReacceptOn(null) }}
        onConfirm={handleConfirmReacceptance}
        title="Solicitar re-aceptación de consentimiento"
        confirmLabel="Solicitar"
        cancelLabel="Cancelar"
        variant="warning"
        isLoading={reaccepting}
      >
        {reacceptOn
          ? `Se limpiará el consentimiento firmado por ${reacceptOn.legalName} (RUC ${reacceptOn.ruc}). El registrador deberá descargar la plantilla vigente, firmarla y volver a subirla. Se registrará una observación.`
          : ''}
      </ConfirmDialog>

      <ConfirmDialog
        open={!!deletingOn}
        onClose={() => { if (!deleting) setDeletingOn(null) }}
        onConfirm={handleConfirmDelete}
        title="Eliminar proveedor"
        confirmLabel="Eliminar"
        cancelLabel="Cancelar"
        variant="danger"
        isLoading={deleting}
      >
        {deletingOn ? `¿Eliminar a ${deletingOn.legalName} (RUC ${deletingOn.ruc})? Esta acción no se puede deshacer.` : ''}
      </ConfirmDialog>

      {creatingMROn && (
        <Box
          position="fixed" top={0} left={0} right={0} bottom={0}
          bg="rgba(0,0,0,0.45)" zIndex={50}
          display="flex" alignItems="center" justifyContent="center"
          onClick={closeCreateMR}
        >
          <Box
            bg={colors.bgCard} border="1px solid" borderColor={colors.border}
            borderRadius="2xl" p={6} maxW="540px" w="90%"
            onClick={(e) => e.stopPropagation()}
          >
            <Text fontSize="lg" fontWeight="bold" color={colors.text} mb={1}>
              Solicitar modificación
            </Text>
            <Text fontSize="sm" color={colors.textMuted} mb={4}>
              {creatingMROn.legalName} · RUC {creatingMROn.ruc}
            </Text>
            <Text fontSize="xs" color={colors.textMuted} fontWeight="700" textTransform="uppercase" letterSpacing="0.05em" mb={2}>
              Motivo
            </Text>
            <Box as="textarea"
              rows={4}
              value={createMRReason}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setCreateMRReason(e.target.value)}
              placeholder="Describe brevemente el cambio que necesitas hacer..."
              w="100%" p={3} borderRadius="md"
              bg={colors.bgCard} border="1px solid" borderColor={colors.border}
              fontSize="sm" color={colors.text}
              _placeholder={{ color: colors.textMuted }}
              _focus={{ borderColor: brand.green, outline: 'none' }}
            />
            <Flex gap={3} justifyContent="flex-end" mt={5}>
              <Box as="button" type="button"
                px={4} py={2} borderRadius="md"
                border="1px solid" borderColor={colors.border}
                color={colors.text} fontSize="sm" fontWeight="600"
                cursor={creatingMR ? 'not-allowed' : 'pointer'}
                opacity={creatingMR ? 0.5 : 1}
                _hover={creatingMR ? {} : { bg: colors.bgHover }}
                onClick={closeCreateMR}
                disabled={creatingMR}
              >
                Cancelar
              </Box>
              <Box as="button" type="button"
                px={4} py={2} borderRadius="md"
                bg={creatingMR ? '#b5aa9d' : brand.green} color="white"
                fontSize="sm" fontWeight="700"
                cursor={creatingMR ? 'not-allowed' : 'pointer'}
                _hover={creatingMR ? {} : { opacity: 0.9 }}
                onClick={handleCreateMR}
                disabled={creatingMR}
              >
                {creatingMR ? 'Creando...' : 'Crear y abrir editor'}
              </Box>
            </Flex>
          </Box>
        </Box>
      )}
    </VStack>
  )
}
